import React, { useLayoutEffect, useState } from 'react';
import { Apple, Smartphone, X } from 'lucide-react';
import { IS_NATIVE } from '@/hooks/useNativePlatform';
import { useIsMobile } from '@/hooks/use-mobile';
import { openExternalUrl } from '@/hooks/useExternalLink';
import { Button } from '@/components/ui/button';

const IOS_STORE_URL = 'https://apps.apple.com/us/app/the-vent-app/id6757621490';
const ANDROID_STORE_URL = 'https://play.google.com/store/apps/details?id=com.ventapp.vent';

const DISMISS_KEY = 'mobile_app_banner_dismissed';
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const BANNER_HEIGHT = 56;

type DeviceType = 'ios' | 'android' | 'other';

function getDeviceType(): DeviceType {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'other';
}

function readDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const { timestamp } = JSON.parse(raw) as { timestamp?: number };
    if (!timestamp) return false;
    return Date.now() - timestamp < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

function writeDismissed() {
  try {
    localStorage.setItem(DISMISS_KEY, JSON.stringify({ timestamp: Date.now() }));
  } catch {
    // ignore
  }
}

export const MobileAppBanner: React.FC = () => {
  const isMobile = useIsMobile();
  const [visible, setVisible] = useState(() => !readDismissed());

  useLayoutEffect(() => {
    const shouldShow = !IS_NATIVE && isMobile && visible;
    document.documentElement.style.setProperty(
      '--mobile-banner-height',
      shouldShow
        ? `calc(env(safe-area-inset-top, 0px) + ${BANNER_HEIGHT}px)`
        : 'env(safe-area-inset-top, 0px)'
    );
    return () => {
      document.documentElement.style.setProperty(
        '--mobile-banner-height',
        'env(safe-area-inset-top, 0px)'
      );
    };
  }, [isMobile, visible]);

  if (IS_NATIVE || !isMobile || !visible) {
    return null;
  }

  const device = getDeviceType();
  const storeUrl =
    device === 'ios' ? IOS_STORE_URL : device === 'android' ? ANDROID_STORE_URL : IOS_STORE_URL;
  const Icon = device === 'ios' ? Apple : Smartphone;
  const label =
    device === 'ios'
      ? 'Download on the App Store'
      : device === 'android'
        ? 'Get it on Google Play'
        : 'Download the app';

  const handleDownload = () => {
    void openExternalUrl(storeUrl);
  };

  const handleDismiss = () => {
    setVisible(false);
    writeDismissed();
  };

  return (
    <div
      className="fixed top-[env(safe-area-inset-top,0px)] left-0 right-0 z-[60] h-14 bg-primary text-primary-foreground flex items-center px-4 gap-3 shadow-sm"
      role="banner"
      aria-label="Download the app"
    >
      <button
        onClick={handleDownload}
        className="flex items-center gap-2 flex-1 min-w-0 text-left"
        aria-label={label}
      >
        <Icon className="h-5 w-5 shrink-0" />
        <span className="text-sm font-medium truncate">{label}</span>
      </button>
      <Button
        variant="ghost"
        size="icon"
        onClick={handleDismiss}
        className="h-8 w-8 shrink-0 text-primary-foreground hover:bg-primary-foreground/10"
        aria-label="Close download banner"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
};
